/* eslint-disable @typescript-eslint/no-use-before-define */

import isArray from 'lodash/isArray';
import isNumber from 'lodash/isNumber';
import flatten from 'lodash/flatten';

import {
  ClientQueryTrackingProps,
  commonTrackingEventPropertyName,
  getNumCustomBucketGroupBys,
  QueryTrackingPropName,
} from 'common/report/tracking-setup';
import {MS_BY_UNIT} from 'common/util/date';
import {EventSegment, UserSegment} from 'common/query-builder/query-block/segment-menu/types';
import {ChartType, FunnelBookmark} from 'common/report/funnels/models/types';
import {getRelativeDateFromComparisonDate} from 'common/query-builder/query-block/time-comparison-menu/utils';
import {isSegmentationFilter, Segfilter} from 'common/widgets/property-filter-menu/models/segfilter';
import {isGroupedEntry} from 'common/query-builder/query-entry-section/types';
import {FunnelsQuery} from 'common/report/funnels/models/funnels-query';
import {LIFT_TRACKING_PREFIX, LiftTypeTrackingName, TIME_COMPARE_NON_LIFT_KEY} from 'common/report/util/lift/types';
import {ComparisonDate} from 'common/query-builder/query-block/time-comparison-menu/types';
import {LiftComparison} from 'common/widgets/lift-comparison-menu/types';
import {ReportName} from 'common/report/constants';

import {ConversionWindowUnit, FunnelOrder} from '../models/constants';

/**
 * Gets both lift and compare to past tracking data
 * @param comparedDateRange
 * @param liftComparison
 */
function getComparisonTrackingData(comparedDateRange: ComparisonDate, liftComparison: LiftComparison) {
  const comparedToPast = !!comparedDateRange;
  let comparisonTrackingData = {};
  if (comparedToPast) {
    const relativeComparison = getRelativeDateFromComparisonDate(comparedDateRange);
    comparisonTrackingData = {
      'Offset Value': relativeComparison.value,
      'Offset Unit': comparedDateRange.offset ? relativeComparison.unit : `Custom`,
    };
  }

  let liftTrackingData = {};
  if (comparedToPast || liftComparison) {
    liftTrackingData = {
      [LIFT_TRACKING_PREFIX]: LiftTypeTrackingName[liftComparison?.type || TIME_COMPARE_NON_LIFT_KEY],
    };
  }
  return {comparedToPast, comparisonTrackingData, liftTrackingData};
}

/**
 * Takes a funnels bookmark as input
 */
export function getFunnelQueryTrackingProps(query: FunnelsQuery): ClientQueryTrackingProps {
  const {
    chartType,
    events,
    eventFilters,
    funnelOrder,
    userFilters,
    comparedDateRange,
    eventIndex,
    trendType,
    frequencyType,
    ttcValueRepresentation,
    frequencyValueRepresentation,
    liftComparison,
  } = query;
  const segmentNames = query.getSegmentNames();
  const numberOfBreakdown = segmentNames.length;
  const numberOfFilters =
    eventFilters.length + userFilters.length + events.reduce((total, ev) => total + ev.filters.length, 0);
  const isAnyOrderFunnel = funnelOrder === FunnelOrder.Any;
  const numberOfAnchoredEvents = isAnyOrderFunnel
    ? events.reduce((total, ev) => total + (ev.entryIndexLabelToggle ? 0 : 1), 0)
    : events.length;
  const numberOfEventsCompared = events.reduce((total, ev) => total + (isGroupedEntry(ev) ? ev.event.length : 0), 0);
  const numberOfStepsCompared = events.reduce((total, ev) => total + (isGroupedEntry(ev) ? 1 : 0), 0);
  const stepSelectionStr = isNumber(eventIndex) ? `Step ${eventIndex} to Step ${eventIndex + 1}` : `All steps`;
  const conversionWindowLength = query.conversionWindow.value;
  const conversionWindowUnit = query.conversionWindow.unit;
  const numSteps = query.events.length;
  const numHoldPropConstants = query.holdingPropertyConstant ? query.holdingPropertyConstant.length : 0;
  const numExclusionSteps = query.exclusionSteps ? query.exclusionSteps.length : 0;

  let bucket = {bucketSize: null, max: null, min: null};
  let viewType = null;
  let valueRepresentation = null;
  let bucketVizType = null;
  let visibleMetrics = [];
  let hiddenMetrics = [];
  let trendUnit = null;
  let trendCountType = null;
  switch (chartType) {
    case ChartType.Chart:
      if (query.selectedTableColumns) {
        visibleMetrics = Object.keys(query.selectedTableColumns).filter((item) => query.selectedTableColumns[item]);
        hiddenMetrics = Object.keys(query.selectedTableColumns).filter((item) => !query.selectedTableColumns[item]);
      }
      bucketVizType = query.stepsVizType;
      break;
    case ChartType.Frequency:
      bucket = query.frequencyBuckets;
      bucketVizType = query.frequencyVizType;
      viewType = frequencyType;
      valueRepresentation = frequencyValueRepresentation;
      break;
    case ChartType.TimeToConvert:
      bucket = query.timeBuckets;
      bucketVizType = query.ttcVizType;
      valueRepresentation = ttcValueRepresentation;
      break;
    case ChartType.Trends:
      viewType = trendType;
      trendUnit = query.trendUnit;
      trendCountType = query.trendCountType;
  }
  const {comparedToPast, comparisonTrackingData, liftTrackingData} = getComparisonTrackingData(
    comparedDateRange,
    liftComparison,
  );

  return {
    'Conversion window length': conversionWindowLength,
    'Conversion window unit': conversionWindowUnit,
    [QueryTrackingPropName.ChartType]: chartType,
    [QueryTrackingPropName.ChartVizType]: bucketVizType,
    'Display Unit': valueRepresentation,
    data_group_id: query.dataGroupId,
    'num steps': numSteps,
    '# of hold prop constants': numHoldPropConstants,
    '# of exclusion steps': numExclusionSteps,
    [commonTrackingEventPropertyName.numCustomBucketGroupBys]: getNumCustomBucketGroupBys(query.groupBy),
    num_group_by_custom_properties: getNumGroupByCustomProperties(query),
    'report info: # custom property group-bys': getNumGroupByCustomProperties(query),
    'report info: # custom property filters': getNumFilterByCustomProperties(query),
    'report info: # custom property hpc': getNumHoldPropConstantCustomProperties(query),
    ...(trendCountType ? {'report info: analysis': trendCountType} : {}),
    ...(trendUnit ? {'report info: time aggregation unit': trendUnit} : {}),
    'Funnel Count Type': query.countType,
    ...(query.countType === `session`
      ? {}
      : {'Funnel Conversion Window Length (MS)': getMSFromLengthUnit(conversionWindowLength, conversionWindowUnit)}),
    max_filters: getMaximumFilters(query),
    'is owner': false,
    '# of filters': numberOfFilters,
    '# of breakdowns': numberOfBreakdown,
    'Breakdown List': segmentNames,
    ...(numberOfBreakdown > 0 ? {'Breakdown 1': segmentNames[0]} : {}),
    ...(numberOfBreakdown > 1 ? {'Breakdown 2': segmentNames[1]} : {}),
    ...(numberOfBreakdown > 2 ? {'Breakdown 3': segmentNames[2]} : {}),
    'Selected chart view': viewType,
    Order: funnelOrder,
    '# of anchored steps': numberOfAnchoredEvents,
    ...(isAnyOrderFunnel ? {'# of unanchored steps': events.length - numberOfAnchoredEvents} : {}),
    'Step selection': stepSelectionStr,
    'Chart interval size': bucket.bucketSize,
    'Chart interval maximum': bucket.max,
    'Chart interval minimum': bucket.min,
    'Compare to Past': comparedToPast,
    '# of steps with comparisons': numberOfStepsCompared,
    'Table hidden metrics': hiddenMetrics,
    'Table visible metrics': visibleMetrics,
    ...(numberOfStepsCompared > 0 ? {'# of events compared': numberOfEventsCompared} : {}),
    ...comparisonTrackingData,
    ...getDataGroupTrackingData(query),
    ...getLookupPropertiesTrackingData(query.generateBookmark()),
    ...liftTrackingData,
  };
}

function getDataGroupTrackingData(queryOrBookmark: FunnelBookmark | FunnelsQuery) {
  if (queryOrBookmark instanceof FunnelsQuery) {
    const query = queryOrBookmark;
    return getDataGroupTrackingProperties(
      query.dataGroupId,
      query.filters.some((f) => f.dataGroupId),
      query.groupBy.some(userFilterHasDataGroupId),
    );
  } else {
    const {data_group_id: dataGroupId, group_by: groupBy, steps} = queryOrBookmark;
    const filterByGroupProperty = (steps || []).some((step) =>
      (`property_filter_params_list` in step ? step.property_filter_params_list : []).some((filter) =>
        Boolean(isSegmentationFilter(filter) ? filter.dataGroupId : filter.property.data_group_id),
      ),
    );
    const segmentByGroupProperty = !!(groupBy && groupBy.some(userFilterHasDataGroupId));
    return getDataGroupTrackingProperties(dataGroupId, filterByGroupProperty, segmentByGroupProperty);
  }

  // -- helpers

  function userFilterHasDataGroupId(segment) {
    if (!(`user` in segment)) {
      return false;
    }

    return !!segment.user.dataGroupId;
  }

  function getDataGroupTrackingProperties(
    dataGroupId: string | number,
    filterByGroupProperty: boolean,
    segmentByGroupProperty: boolean,
  ) {
    return {
      [commonTrackingEventPropertyName.dataGroupId]: dataGroupId || null,
      [commonTrackingEventPropertyName.filterByGroupProperty]: filterByGroupProperty,
      [commonTrackingEventPropertyName.segmentByGroupProperty]: segmentByGroupProperty,
      [commonTrackingEventPropertyName.reportName]: ReportName.Funnels,
    };
  }
}

function isLookupProperty(prop) {
  if (!prop) {
    return false;
  }
  const hasDataGroupId = !!(prop.dataGroupId || prop.data_group_id);
  const value = prop.name || prop.propertyName;
  return hasDataGroupId && isArray(value);
}

function getLookupPropertiesTrackingData(bookmark: FunnelBookmark) {
  const {group_by: groupBy, aggregate_by: holdingPropertyConstant, steps} = bookmark;

  const numberOfLookupTablePropertyGroupBys = groupBy
    ? groupBy.filter((segment) =>
        [(segment as EventSegment).event, (segment as UserSegment).user].some(isLookupProperty),
      ).length
    : 0;

  const numberOfLookupTableHoldPropConstant = holdingPropertyConstant
    ? holdingPropertyConstant.filter((segment) => isLookupProperty(segment)).length
    : 0;

  const numberOfLookupTablePropertyFilters = (steps || []).reduce(
    (acc, step) => acc + getParams(step).filter((filterParam) => isLookupProperty(filterParam.property)).length,
    0,
  );

  return {
    [commonTrackingEventPropertyName.numberOfLookupTablePropertyGroupBys]: numberOfLookupTablePropertyGroupBys,
    [commonTrackingEventPropertyName.numberOfLookupTablePropertyFilters]: numberOfLookupTablePropertyFilters,
    [commonTrackingEventPropertyName.numberOfLookupTablePropertyHoldPropConstant]: numberOfLookupTableHoldPropConstant,
    [commonTrackingEventPropertyName.usedLookupTableProperties]:
      numberOfLookupTablePropertyGroupBys + numberOfLookupTablePropertyFilters > 0,
  };

  function getParams(step): Segfilter[] {
    if (`property_filter_params_list` in step) {
      return step.property_filter_params_list;
    } else {
      return [];
    }
  }
}

function getNumGroupByCustomProperties(query: FunnelsQuery) {
  const groupBys = query.groupBy || [];
  return groupBys.filter((segment) =>
    []
      .concat((segment as EventSegment).event, (segment as UserSegment).user)
      .some((data) => data && (data.customProperty || data.customPropertyId)),
  ).length;
}

function getNumFilterByCustomProperties(query: FunnelsQuery) {
  const stepsFilters = flatten(query.events.concat(query.exclusionSteps).map((step) => step.filters || []));

  const filters = [...stepsFilters, ...query.eventFilters, ...query.userFilters];

  return filters.filter((filter) => filter.customProperty || filter.customPropertyId).length;
}

function getNumHoldPropConstantCustomProperties(query: FunnelsQuery) {
  const holdPropConstant = query.holdingPropertyConstant || [];
  return holdPropConstant.filter((segment) => segment && (segment.customProperty || segment.customPropertyId)).length;
}

function getMaximumFilters(query) {
  const steps = query.steps || query.events;
  const exclusions = query.exclusions || [];
  // Using appState.query here which has exclusionsSteps, not exclusions
  const exclusionSteps = query.exclusionSteps || [];
  return Math.max(
    ...steps.map((s) => (s.property_filter_params_list ? s.property_filter_params_list.length : 0)),
    ...exclusions.map((es) => (es.property_filter_params_list ? es.property_filter_params_list.length : 0)),
    ...exclusionSteps.map((es) => (es.filters ? es.filters.length : 0)),
  );
}

export function getNumCohortFilters(funnelParams) {
  // For now we only support one cohort filter
  const {name} = JSON.parse(funnelParams.filter_by_cohort || `{}`);
  return name === undefined ? 0 : 1;
}

function getMSFromLengthUnit(length: number, lengthUnit: ConversionWindowUnit): number | void {
  // error checking exists to prevent saving NaN and incorrect info in tracking
  if (Number.isNaN(length) || !lengthUnit) {
    return null;
  } else {
    return length * MS_BY_UNIT[lengthUnit];
  }
}

// TODO: move everything into here
export enum TrackedEvents {
  /** Toggled between specific/any order funnels */
  AnyOrderToggled = `Any order toggle`,
  EditBucketSelector = `Edit Interval`,
  /** select a step/event index */
  StepSelected = `Step selected`,
  /** Chart selector clicked */
  ChartSelected = `Chart Selector`,
  /** Trends: new trend view is selected */
  ChartViewSelected = `Select chart view`,
  /** TTC/Frequency: Visualization is selected ie Bar -> Line */
  ChartVisualizationSelected = `Edit chart visualization`,
  /** Submitted ODA analysis request */
  ODASubmittedRequest = `submitted analysis request`,
  /** Time-to-convert: reset conversion time span */
  TTCReset = `reset time to convert`,
  /** Toggled trend count type */
  TrendCountTypeToggled = `Toggled Trends Count Type`,
  /** View as flows button clicked */
  ViewAsFlowsClicked = `DA View as flows clicked`,
  /** Table Metric Visibility Toggled */
  HideTableMetric = `Hide table metric`,
  ShowTableMetric = `Show table metric`,
}
